import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, TrendingUp, AlertTriangle, CheckCircle2, Clock, Users, Building2, Sparkles, Award, ThumbsDown, Download } from 'lucide-react';
import { analyticsApi, type Dashboard, type WeeklyPoint, type AgentRating, type AgentSla, type EscalationsOut } from '../api/extras';
import { api } from '../api/client';
import { authApi } from '../api/auth';
import { useAuth, type AuthUser } from '../lib/auth';
import { Link, Navigate } from 'react-router-dom';
import { Markdown } from '../lib/markdown';

const RANGES = [7, 14, 30, 60, 90, 180, 365];

function daysFromStartOfMonth(): number {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return Math.max(1, Math.ceil((now.getTime() - start.getTime()) / (24 * 3600 * 1000)));
}
function daysFromStartOfQuarter(): number {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  const start = new Date(now.getFullYear(), q * 3, 1);
  return Math.max(1, Math.ceil((now.getTime() - start.getTime()) / (24 * 3600 * 1000)));
}
function daysFromStartOfYear(): number {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  return Math.max(1, Math.ceil((now.getTime() - start.getTime()) / (24 * 3600 * 1000)));
}

const CALENDAR_RANGES: Array<{ label: string; calc: () => number }> = [
  { label: 'Месяц', calc: daysFromStartOfMonth },
  { label: 'Квартал', calc: daysFromStartOfQuarter },
  { label: 'Год', calc: daysFromStartOfYear },
];

export default function DashboardPage() {
  const { user } = useAuth();
  if (user?.type !== 'staff') return <Navigate to="/" replace />;
  const [d, setD] = useState<Dashboard | null>(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const r = await api.get(`/api/v1/analytics/export.xlsx?days=${days}`,
        { responseType: 'blob' });
      const url = URL.createObjectURL(r.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `sd-bi-${stamp}-${days}d.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } finally { setExporting(false); }
  };

  useEffect(() => {
    setLoading(true);
    analyticsApi.dashboard(days).then(setD).finally(() => setLoading(false));
  }, [days]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="text-blue-400" size={20} />
        <h1 className="text-2xl font-bold text-white">Дашборд</h1>
        <div className="ml-auto flex gap-1 flex-wrap items-center">
          {RANGES.map(r => (
            <button key={r} onClick={() => setDays(r)}
              className={`px-2.5 py-1 rounded text-xs font-bold uppercase transition ${
                days === r ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}>
              {r} дн
            </button>
          ))}
          <span className="mx-1 text-slate-700">|</span>
          {CALENDAR_RANGES.map(r => {
            const v = r.calc();
            return (
              <button key={r.label} onClick={() => setDays(v)}
                title={`От начала ${r.label.toLowerCase()}а (${v} дн)`}
                className={`px-2.5 py-1 rounded text-xs font-bold uppercase transition ${
                  days === v ? 'bg-purple-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}>
                {r.label}
              </button>
            );
          })}
          <span className="mx-1 text-slate-700">|</span>
          <Link to="/risk"
                title="Контракты под риском — приоритеты для аккаунт-менеджера"
                className="px-2.5 py-1 rounded text-xs font-bold uppercase transition bg-orange-700 hover:bg-orange-600 text-white inline-flex items-center gap-1">
            <AlertTriangle size={12} /> Риски
          </Link>
          <button onClick={exportXlsx} disabled={exporting}
            title={`Выгрузить BI-отчёт в Excel за ${days} дней`}
            className="px-2.5 py-1 rounded text-xs font-bold uppercase transition bg-emerald-700 hover:bg-emerald-600 disabled:bg-slate-700 text-white inline-flex items-center gap-1">
            {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
            Excel
          </button>
        </div>
      </div>

      {loading || !d ? (
        <Loader2 className="animate-spin text-blue-400 mx-auto mt-10" />
      ) : (
        <div className="space-y-4">
          {/* Totals */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Tile label="Создано" value={d.totals.created} icon={<TrendingUp size={14} />} color="text-blue-300" />
            <Tile label="Решено" value={d.totals.resolved} icon={<CheckCircle2 size={14} />} color="text-emerald-300" />
            <Tile label="Закрыто" value={d.totals.closed} icon={<CheckCircle2 size={14} />} color="text-slate-300" />
            <Tile label="Breach FRT" value={d.totals.breach_frt} icon={<AlertTriangle size={14} />} color="text-orange-300" />
            <Tile label="Breach TTR" value={d.totals.breach_ttr} icon={<AlertTriangle size={14} />} color="text-red-300" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Tile label="Среднее время первого ответа (FRT)"
                  value={d.avg_frt_minutes != null ? humanMin(d.avg_frt_minutes) : '—'}
                  icon={<Clock size={14} />} color="text-blue-300" big />
            <Tile label="Среднее время решения (TTR)"
                  value={d.avg_ttr_minutes != null ? humanMin(d.avg_ttr_minutes) : '—'}
                  icon={<Clock size={14} />} color="text-emerald-300" big />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Distribution title="По приоритетам" data={d.by_priority} order={['P1','P2','P3','P4']}
                          colors={{P1:'bg-red-500',P2:'bg-orange-500',P3:'bg-blue-500',P4:'bg-slate-500'}} />
            <Distribution title="По линиям" data={d.by_line} order={['1','2','3','none']}
                          labels={{'1':'L1','2':'L2','3':'L3','none':'без линии'}}
                          colors={{'1':'bg-emerald-500','2':'bg-yellow-500','3':'bg-purple-500','none':'bg-slate-600'}} />
            <Distribution title="По статусам" data={d.by_status}
                          order={['new','open','pending_customer','on_hold','resolved','closed','cancelled']}
                          colors={{new:'bg-blue-500',open:'bg-yellow-500',pending_customer:'bg-orange-500',
                                   on_hold:'bg-purple-500',resolved:'bg-emerald-500',closed:'bg-slate-600',cancelled:'bg-red-500'}} />
          </div>

          {/* Top organizations */}
          <section className="bg-slate-900 border border-slate-800 rounded-xl">
            <header className="px-5 py-3 border-b border-slate-800 flex items-center gap-2">
              <Building2 size={14} className="text-orange-400" />
              <h2 className="font-bold text-white">Активность клиентов — топ-10 по нагрузке на поддержку</h2>
              <span className="text-[11px] text-slate-500 ml-2">(тикетов + 2× breach)</span>
            </header>
            <div className="p-3">
              {d.top_organizations.length === 0 ? (
                <div className="text-sm text-slate-500 py-4 text-center">Нет данных за период.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-[10px] text-slate-500 uppercase tracking-wider">
                    <tr>
                      <th className="text-left py-2 px-2">Организация</th>
                      <th className="text-right py-2 px-2">Тикетов</th>
                      <th className="text-right py-2 px-2">Breach</th>
                      <th className="text-right py-2 px-2">Нагрузка</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.top_organizations.map(o => (
                      <tr key={o.organization_id} className="border-t border-slate-800">
                        <td className="py-2 px-2">
                          <Link to={`/organizations/${o.organization_id}`}
                            className="text-blue-400 hover:underline">{o.name}</Link>
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-slate-300">{o.created}</td>
                        <td className="py-2 px-2 text-right font-mono text-red-300">{o.breach}</td>
                        <td className="py-2 px-2 text-right font-mono font-bold text-emerald-300">{o.score}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          {/* Top agents */}
          <section className="bg-slate-900 border border-slate-800 rounded-xl">
            <header className="px-5 py-3 border-b border-slate-800 flex items-center gap-2">
              <Users size={14} className="text-blue-400" />
              <h2 className="font-bold text-white">Топ-агенты (закрыто тикетов)</h2>
            </header>
            <div className="p-3">
              {d.top_agents.length === 0 ? (
                <div className="text-sm text-slate-500 py-4 text-center">Нет данных за период.</div>
              ) : (
                <ul className="divide-y divide-slate-800">
                  {d.top_agents.map(a => (
                    <li key={a.user_id} className="flex items-center justify-between py-2 px-2 hover:bg-slate-800/30">
                      <Link to={`/?agent_id=${a.user_id}`}
                            title="Открыть Очередь с фильтром по этому агенту"
                            className="text-sm text-blue-400 hover:text-blue-300">{a.name}</Link>
                      <Link to={`/?agent_id=${a.user_id}`} className="font-mono text-sm text-emerald-400">{a.resolved}</Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <WeeklyTrendsSection />

          <AgentsRatingSection days={days} />

          <AgentsSlaSection days={days} />

          <EscalationsSection days={days} />

          <AiReportSection days={days} />
        </div>
      )}
    </div>
  );
}


// ── Weekly trends ──────────────────────────────────────────────────────────
function WeeklyTrendsSection() {
  const [data, setData] = useState<WeeklyPoint[]>([]);
  const [weeks, setWeeks] = useState(52);
  const [agentId, setAgentId] = useState<string>('');
  const [agents, setAgents] = useState<AuthUser[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { authApi.staff().then(setAgents).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    analyticsApi.weekly(weeks, agentId || undefined)
      .then(setData).finally(() => setLoading(false));
  }, [weeks, agentId]);

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="px-5 py-3 border-b border-slate-800 flex items-center gap-2 flex-wrap">
        <TrendingUp size={14} className="text-blue-400" />
        <h2 className="font-bold text-white">Динамика по неделям</h2>
        <div className="ml-auto flex items-center gap-2">
          <select value={agentId} onChange={e => setAgentId(e.target.value)}
            className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-white max-w-[200px]">
            <option value="">Все агенты</option>
            {agents.map(a => <option key={a.id} value={a.id}>{a.full_name || a.email}</option>)}
          </select>
          <div className="flex gap-1">
            {[12, 26, 52].map(w => (
              <button key={w} onClick={() => setWeeks(w)}
                className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${
                  weeks === w ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}>{w} нед</button>
            ))}
          </div>
        </div>
      </header>
      <div className="p-4">
        {loading ? <Loader2 className="animate-spin text-blue-400 mx-auto my-6" /> :
         <LineChart data={data} />}
        <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-slate-400">
          <Legend color="bg-blue-400" label="Создано" />
          <Legend color="bg-emerald-400" label="Решено" />
          <Legend color="bg-slate-400" label="Закрыто" />
          <Legend color="bg-red-400" label="Просрочено (breach)" />
        </div>
      </div>
    </section>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`inline-block w-3 h-1 ${color} rounded`} />
      <span>{label}</span>
    </div>
  );
}

// SVG-чарт: 4 линии. Высота 220, ширина адаптивная (300..900).
function LineChart({ data }: { data: WeeklyPoint[] }) {
  if (data.length === 0) return <div className="text-sm text-slate-500 py-8 text-center">Нет данных</div>;
  const W = 900, H = 220, PAD_L = 35, PAD_R = 15, PAD_T = 10, PAD_B = 30;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const n = data.length;
  const maxV = Math.max(1, ...data.flatMap(d => [d.created, d.resolved, d.closed, d.breach]));
  const x = (i: number) => PAD_L + (n === 1 ? innerW / 2 : (i * innerW) / (n - 1));
  const y = (v: number) => PAD_T + innerH - (v / maxV) * innerH;
  const series = [
    { k: 'created', color: '#60a5fa' },   // blue-400
    { k: 'resolved', color: '#34d399' },  // emerald-400
    { k: 'closed', color: '#94a3b8' },    // slate-400
    { k: 'breach', color: '#f87171' },    // red-400
  ] as const;
  const yTicks = [0, Math.floor(maxV / 2), maxV];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
      {/* Сетка по Y */}
      {yTicks.map(t => (
        <g key={t}>
          <line x1={PAD_L} y1={y(t)} x2={W - PAD_R} y2={y(t)} stroke="#1e293b" strokeDasharray="3 3" />
          <text x={PAD_L - 5} y={y(t)} fill="#64748b" fontSize="10" textAnchor="end" dominantBaseline="middle">{t}</text>
        </g>
      ))}
      {/* Подписи недель — каждая 4-я */}
      {data.map((d, i) => (
        i % Math.ceil(n / 12) === 0 ? (
          <text key={d.week} x={x(i)} y={H - 8} fill="#64748b" fontSize="9" textAnchor="middle">
            {d.week.split('-W')[1]}
          </text>
        ) : null
      ))}
      {/* Линии */}
      {series.map(s => {
        const pts = data.map((d, i) => `${x(i)},${y((d as any)[s.k])}`).join(' ');
        return (
          <g key={s.k}>
            <polyline points={pts} fill="none" stroke={s.color} strokeWidth="2"
                      strokeLinejoin="round" strokeLinecap="round" />
            {data.map((d, i) => (
              <circle key={i} cx={x(i)} cy={y((d as any)[s.k])} r="2.5" fill={s.color}>
                <title>{`${d.week} · ${s.k} = ${(d as any)[s.k]}`}</title>
              </circle>
            ))}
          </g>
        );
      })}
    </svg>
  );
}


// ── Agents rating ───────────────────────────────────────────────────────────
function AgentsRatingSection({ days }: { days: number }) {
  const [data, setData] = useState<{ most_active: AgentRating[]; least_effective: AgentRating[] } | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    analyticsApi.agentsRating(days).then(setData).finally(() => setLoading(false));
  }, [days]);
  if (loading) return <Loader2 className="animate-spin text-blue-400 mx-auto my-4" />;
  if (!data) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <RatingTable title="Самые активные" items={data.most_active} icon={<Award size={14} className="text-emerald-400" />} positive />
      <RatingTable title="Самые неэффективные" items={data.least_effective} icon={<ThumbsDown size={14} className="text-red-400" />} />
    </div>
  );
}

function RatingTable({ title, items, icon, positive }:
  { title: string; items: AgentRating[]; icon: React.ReactNode; positive?: boolean }) {
  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="px-5 py-3 border-b border-slate-800 flex items-center gap-2">
        {icon}
        <h3 className="font-bold text-white text-sm">{title}</h3>
      </header>
      <div className="p-2">
        {items.length === 0 ? (
          <div className="text-sm text-slate-500 py-4 text-center">Нет данных за период</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-[10px] text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="text-left py-1 px-2">Агент</th>
                <th className="text-right py-1 px-2">Решено</th>
                <th className="text-right py-1 px-2">Breach</th>
                <th className="text-right py-1 px-2">Рейтинг</th>
                <th className="text-right py-1 px-2">Score</th>
              </tr>
            </thead>
            <tbody>
              {items.map(a => (
                <tr key={a.user_id} className="border-t border-slate-800 hover:bg-slate-800/30">
                  <td className="py-1.5 px-2 truncate">
                    <Link to={`/?agent_id=${a.user_id}`} className="text-blue-400 hover:text-blue-300">{a.name}</Link>
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono text-emerald-400">{a.resolved}</td>
                  <td className="py-1.5 px-2 text-right font-mono text-red-400">{a.breach}</td>
                  <td className="py-1.5 px-2 text-right font-mono text-yellow-400">
                    {a.avg_rating != null ? `${a.avg_rating} (${a.rating_count})` : '—'}
                  </td>
                  <td className={`py-1.5 px-2 text-right font-mono font-bold ${
                    positive ? 'text-emerald-300' :
                    a.score < 0 ? 'text-red-300' : 'text-slate-300'
                  }`}>{a.score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}


// ── SLA по агентам (full table) ─────────────────────────────────────────────
function AgentsSlaSection({ days }: { days: number }) {
  const [items, setItems] = useState<AgentSla[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    analyticsApi.agentsSla(days).then(r => setItems(r.items)).finally(() => setLoading(false));
  }, [days]);

  const fmtRate = (r: number) => `${(r * 100).toFixed(0)}%`;
  const rateColor = (r: number) => r === 0 ? 'text-slate-500'
    : r < 0.05 ? 'text-emerald-400'
    : r < 0.15 ? 'text-yellow-400'
    : r < 0.30 ? 'text-orange-400'
    : 'text-red-400';
  const fmtMin = (m: number | null) => m == null ? '—'
    : m < 60 ? `${m}м`
    : `${Math.floor(m / 60)}ч ${Math.round(m % 60)}м`;

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="px-5 py-3 border-b border-slate-800 flex items-center gap-2">
        <Clock size={14} className="text-blue-400" />
        <h3 className="font-bold text-white text-sm">SLA по агентам</h3>
        <span className="text-[10px] text-slate-500">за {days} дн</span>
      </header>
      {loading ? <Loader2 className="animate-spin text-blue-400 mx-auto my-4" />
        : items.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-6">Нет активных staff</div>
        ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="text-[10px] text-slate-500 uppercase tracking-wider border-b border-slate-800">
              <tr>
                <th className="text-left py-2 px-3">Агент</th>
                <th className="text-right py-2 px-2">Назначено</th>
                <th className="text-right py-2 px-2">Решено</th>
                <th className="text-right py-2 px-2">Закрыто</th>
                <th className="text-right py-2 px-2">FRT breach</th>
                <th className="text-right py-2 px-2">TTR breach</th>
                <th className="text-right py-2 px-2">Avg FRT</th>
                <th className="text-right py-2 px-2">Avg TTR</th>
                <th className="text-right py-2 px-2">Рейтинг</th>
              </tr>
            </thead>
            <tbody>
              {items.map(a => (
                <tr key={a.user_id} className="border-t border-slate-800/60 hover:bg-slate-800/30">
                  <td className="py-1.5 px-3">
                    <Link to={`/?agent_id=${a.user_id}`}
                          title="Открыть Очередь с фильтром по этому агенту"
                          className="text-blue-400 hover:text-blue-300">{a.name}</Link>
                    <div className="text-[10px] text-slate-500 font-mono">{a.email}</div>
                  </td>
                  <td className="py-1.5 px-2 text-right text-white font-bold">{a.assigned}</td>
                  <td className="py-1.5 px-2 text-right text-emerald-400">{a.resolved}</td>
                  <td className="py-1.5 px-2 text-right text-slate-300">{a.closed}</td>
                  <td className={`py-1.5 px-2 text-right font-mono ${rateColor(a.frt_breach_rate)}`}>
                    {a.frt_breach}
                    {a.assigned > 0 && <span className="text-[9px] ml-1 text-slate-500">{fmtRate(a.frt_breach_rate)}</span>}
                  </td>
                  <td className={`py-1.5 px-2 text-right font-mono ${rateColor(a.ttr_breach_rate)}`}>
                    {a.ttr_breach}
                    {a.assigned > 0 && <span className="text-[9px] ml-1 text-slate-500">{fmtRate(a.ttr_breach_rate)}</span>}
                  </td>
                  <td className="py-1.5 px-2 text-right text-slate-300 font-mono">{fmtMin(a.avg_frt_minutes)}</td>
                  <td className="py-1.5 px-2 text-right text-slate-300 font-mono">{fmtMin(a.avg_ttr_minutes)}</td>
                  <td className="py-1.5 px-2 text-right">
                    {a.avg_rating != null
                      ? <span className="text-yellow-400 font-mono">{a.avg_rating}<span className="text-[9px] text-slate-500"> ({a.rating_count})</span></span>
                      : <span className="text-slate-600">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}


// ── Эскалации в L3 (Redmine) ────────────────────────────────────────────────
function EscalationsSection({ days }: { days: number }) {
  const [data, setData] = useState<EscalationsOut | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    analyticsApi.escalations(days).then(setData).finally(() => setLoading(false));
  }, [days]);
  if (loading) return <Loader2 className="animate-spin text-blue-400 mx-auto my-4" />;
  if (!data) return null;
  const rate = data.rate * 100;
  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="px-5 py-3 border-b border-slate-800 flex items-center gap-2">
        <AlertTriangle size={14} className="text-amber-400" />
        <h3 className="font-bold text-white text-sm">Эскалации в L3 (Redmine)</h3>
        <span className="text-[10px] text-slate-500">за {days} дн</span>
        <span className="ml-auto text-xs text-slate-300">
          <span className="text-white font-bold">{data.total_escalations}</span>
          <span className="text-slate-500"> из {data.total_tickets} ({rate.toFixed(1)}%)</span>
        </span>
      </header>
      {data.total_escalations === 0 ? (
        <div className="text-sm text-slate-500 text-center py-6">За период эскалаций не было</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-3">
          <EscBlock title="Организации" rows={data.by_organization}
                    renderLabel={r => (
                      <a href={`/organizations/${r.key}`}
                         className="text-blue-400 hover:text-blue-300">{r.label}</a>
                    )} />
          <EscBlock title="Кто эскалирует" rows={data.by_assignee}
                    renderLabel={r => (
                      <>
                        <div className="text-slate-200">{r.label}</div>
                        {r.extra?.email && <div className="text-[9px] font-mono text-slate-600">{r.extra.email}</div>}
                      </>
                    )} />
          <EscBlock title="По приоритету" rows={data.by_priority} />
          <EscBlock title="По линии SD" rows={data.by_support_line} />
          <EscBlock title="Текущий статус эскалированных" rows={data.by_status} />
        </div>
      )}
    </section>
  );
}

function EscBlock({ title, rows, renderLabel }:
  { title: string; rows: { key: string; label: string; count: number; extra?: any }[];
    renderLabel?: (r: { key: string; label: string; count: number; extra?: any }) => React.ReactNode }) {
  if (rows.length === 0) return null;
  const max = Math.max(...rows.map(r => r.count));
  return (
    <div className="bg-slate-950/40 border border-slate-800 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">{title}</div>
      <ul className="space-y-1">
        {rows.map(r => (
          <li key={r.key} className="text-xs">
            <div className="flex items-baseline justify-between gap-2">
              <div className="truncate flex-1 min-w-0">
                {renderLabel ? renderLabel(r) : <span className="text-slate-200">{r.label}</span>}
              </div>
              <span className="text-white font-bold font-mono shrink-0">{r.count}</span>
            </div>
            <div className="h-1 mt-0.5 bg-slate-800 rounded overflow-hidden">
              <div className="h-full bg-amber-500/60" style={{ width: `${(r.count / max) * 100}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}


// ── AI-отчёт ───────────────────────────────────────────────────────────────
function AiReportSection({ days }: { days: number }) {
  const [agentId, setAgentId] = useState<string>('');
  const [agents, setAgents] = useState<AuthUser[]>([]);
  const [text, setText] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { authApi.staff().then(setAgents).catch(() => {}); }, []);

  const run = async () => {
    setBusy(true); setErr(null); setText(null);
    try {
      const r = await analyticsApi.aiReport(days, agentId || undefined);
      setText(r.text);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setBusy(false); }
  };

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="px-5 py-3 border-b border-slate-800 flex items-center gap-2 flex-wrap">
        <Sparkles size={14} className="text-purple-400" />
        <h2 className="font-bold text-white">AI-отчёт о эффективности</h2>
        <span className="text-[11px] text-slate-500">за {days} дн</span>
        <div className="ml-auto flex items-center gap-2">
          <select value={agentId} onChange={e => setAgentId(e.target.value)}
            className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-white max-w-[200px]">
            <option value="">Вся команда</option>
            {agents.map(a => <option key={a.id} value={a.id}>{a.full_name || a.email}</option>)}
          </select>
          <button onClick={run} disabled={busy}
            className="text-xs inline-flex items-center gap-1.5 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-700 text-white px-3 py-1.5 rounded">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {text ? 'Перегенерировать' : 'Сгенерировать отчёт'}
          </button>
        </div>
      </header>
      <div className="p-5">
        {err && <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm mb-3">{err}</div>}
        {!text && !busy && !err && (
          <div className="text-sm text-slate-500 text-center py-6">
            Нажмите «Сгенерировать отчёт», AI проанализирует метрики за период и даст оценку.
          </div>
        )}
        {busy && (
          <div className="flex items-center gap-2 text-sm text-slate-400 py-6 justify-center">
            <Loader2 className="animate-spin text-purple-400" />
            AI собирает аналитический отчёт…
          </div>
        )}
        {text && (
          <div className="prose-invert max-w-none"><Markdown source={text} /></div>
        )}
      </div>
    </section>
  );
}

function Tile({ label, value, icon, color, big }:
  { label: string; value: any; icon: React.ReactNode; color: string; big?: boolean }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
        <span className={color}>{icon}</span> {label}
      </div>
      <div className={`mt-1 font-bold ${color} ${big ? 'text-2xl' : 'text-3xl'}`}>{value}</div>
    </div>
  );
}

function Distribution({ title, data, order, colors, labels }:
  { title: string; data: Record<string, number>; order: string[];
    colors: Record<string, string>; labels?: Record<string, string> }) {
  const max = Math.max(1, ...Object.values(data));
  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl p-4">
      <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-3">{title}</h3>
      <ul className="space-y-2">
        {order.map(k => {
          const n = data[k] || 0;
          const pct = (n / max) * 100;
          return (
            <li key={k}>
              <div className="flex items-center justify-between text-[11px] mb-0.5">
                <span className="text-slate-300">{labels?.[k] || k}</span>
                <span className="font-mono text-slate-400">{n}</span>
              </div>
              <div className="h-1.5 bg-slate-800 rounded overflow-hidden">
                <div className={`h-full ${colors[k] || 'bg-slate-500'}`} style={{ width: `${pct}%` }} />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function humanMin(m: number): string {
  if (m < 60) return `${Math.round(m)} мин`;
  const h = m / 60;
  if (h < 24) return `${h.toFixed(1)} ч`;
  const d = h / 24;
  return `${d.toFixed(1)} дн`;
}
